import { useEffect, useRef, useState } from "react";

type CopySnippetIslandProps = {
  text?: string;
  idleLabel?: string;
  successLabel?: string;
  errorLabel?: string;
  className?: string;
};

type CopyState = "idle" | "copied" | "error";

export function CopySnippetIsland(props: CopySnippetIslandProps) {
  const [state, setState] = useState<CopyState>("idle");
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const text = typeof props.text === "string" ? props.text : "";

  async function handleCopy() {
    if (!text.trim()) {
      setState("error");
      scheduleReset();
      return;
    }

    try {
      await copyText(text);
      setState("copied");
    } catch {
      setState("error");
    }
    scheduleReset();
  }

  function scheduleReset() {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(() => {
      setState("idle");
      resetTimerRef.current = null;
    }, 1800);
  }

  const label =
    state === "copied"
      ? props.successLabel || "已复制"
      : state === "error"
        ? props.errorLabel || "复制失败"
        : props.idleLabel || "复制安装说明";

  return (
    <button
      type="button"
      className={
        props.className ||
        "inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-950"
      }
      onClick={() => {
        void handleCopy();
      }}
    >
      <CopyIcon />
      {label}
    </button>
  );
}

function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="6" y="2.75" width="7" height="10.5" rx="1.75" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.25 10.25H4C3.31 10.25 2.75 9.69 2.75 9V4C2.75 3.31 3.31 2.75 4 2.75H8.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("copy failed");
  }
}
